import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan, faUmbrellaBeach, faBriefcase, faEdit, faSave, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { Role } from '../../components/auth-context/role';
import Select from 'react-select';
import api from '../../api/api';
import { globalVariables } from '../../config';
import './user-manager.scss';

export function UserManager() {
    const { t } = useTranslation();
    const [pilots, setPilots] = useState([]);
    const [error, setError] = useState(null);
    const [editingPilot, setEditingPilot] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [availableCallsigns, setAvailableCallsigns] = useState([]);
    const [loadingCallsigns, setLoadingCallsigns] = useState(false);
    const [editForm, setEditForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        callsign: '',
        ivaoId: '',
        vatsimId: ''
    });

    useEffect(() => {
        const fetchPilots = async () => {
            try {
                const response = await api.get('/pilots/admin');
                if (response?.data) {
                    setPilots(response.data);
                    setError(null);
                } else {
                    throw new Error('No pilot data received from server');
                }
            } catch (error) {
                console.error('Failed to fetch pilots:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status,
                });
                const errorMessage =
                    error.response?.data?.error ||
                    error.message ||
                    'Failed to fetch pilots';
                setError(errorMessage);
            }
        };
        fetchPilots();
    }, []);

    const handleDelete = async (pilotId) => {
        if (window.confirm('Are you sure you want to delete this pilot account?')) {
            try {
                await api.delete(`/auth/delete/${pilotId}`);
                setPilots(pilots.filter((pilot) => pilot.id !== pilotId));
                alert('Pilot account deleted successfully');
            } catch (error) {
                console.error('Failed to delete pilot account:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status,
                });
                const errorMessage =
                    error.response?.data?.error ||
                    error.message ||
                    'Failed to delete pilot account';
                setError(errorMessage);
            }
        }
    };

    const toggleVacation = async (pilotId, currentStatus) => {
        try {
            await api.put(`/pilots/${pilotId}`, {
                onVacation: !currentStatus
            });
            // Actualizar el estado local
            setPilots(pilots.map(pilot => 
                pilot.id === pilotId 
                    ? { ...pilot, onVacation: !currentStatus }
                    : pilot
            ));
            alert(`Pilot ${!currentStatus ? 'set to vacation' : 'returned from vacation'} successfully`);
        } catch (error) {
            console.error('Failed to update vacation status:', error);
            setError('Failed to update vacation status');
        }
    };

    const formatLastFlight = (flights) => {
        if (!flights || flights.length === 0) return 'N/A';
        
        const lastFlightDate = new Date(flights[0].createdAt);
        const now = new Date();
        const diffTime = Math.abs(now - lastFlightDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        return `${diffDays} days ago`;
    };

    const fetchAvailableCallsigns = async () => {
        setLoadingCallsigns(true);
        try {
            const response = await api.get('/auth/next-callsign');
            if (response.data.availableCallsigns) {
                // Remove the airline code prefix from callsigns for the selector
                const callsigns = response.data.availableCallsigns.map((cs) =>
                    cs.replace(globalVariables.OACI, '')
                );
                setAvailableCallsigns(callsigns);
            }
        } catch (error) {
            console.error('Failed to fetch available callsigns:', error);
        } finally {
            setLoadingCallsigns(false);
        }
    };

    const handleEdit = async (pilot) => {
        setEditingPilot(pilot.id);
        setEditForm({
            firstName: pilot.firstName || '',
            lastName: pilot.lastName || '',
            email: pilot.email || '',
            callsign: pilot.callsign || '', // Keep the full callsign (EXU001)
            ivaoId: pilot.ivaoId || '',
            vatsimId: pilot.vatsimId || ''
        });
        
        // Fetch available callsigns if pilot doesn't have one
        if (!pilot.callsign) {
            await fetchAvailableCallsigns();
        }
        
        setShowModal(true);
    };

    const handleCancelEdit = () => {
        setEditingPilot(null);
        setShowModal(false);
        setEditForm({
            firstName: '',
            lastName: '',
            email: '',
            callsign: '',
            ivaoId: '',
            vatsimId: ''
        });
        setAvailableCallsigns([]);
    };

    const handleSaveEdit = async (pilotId) => {
        try {
            const response = await api.put(`/pilots/${pilotId}/admin`, editForm);
            
            // Refrescar la lista completa de pilotos para asegurar que tenemos los datos actualizados
            const pilotsResponse = await api.get('/pilots/admin');
            if (pilotsResponse?.data) {
                setPilots(pilotsResponse.data);
            }
            
            setEditingPilot(null);
            setShowModal(false);
            setAvailableCallsigns([]);
            alert('Pilot data updated successfully');
        } catch (error) {
            console.error('Failed to update pilot data:', error);
            const errorMessage = error.response?.data?.error || 'Failed to update pilot data';
            setError(errorMessage);
            alert(errorMessage);
        }
    };

    const handleEditChange = (field, value) => {
        setEditForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <div className="view-model user-manager">
            <h2>{t('user-manager')}</h2>
            <Role has="USER_MANAGER">
                {error && <p className="error-message">{error}</p>}
                <div className="table-container">
                    <table className="pilot-table">
                        <thead>
                        <tr>
                            <th>#ID</th>
                            <th>{t('first-name')}</th>
                            <th>{t('last-name')}</th>
                            <th>{t('email')}</th>
                            <th>{t('callsign')}</th>
                            <th>IVAO ID</th>
                            <th>VATSIM ID</th>
                            <th>{t('status')}</th>
                            <th>{t('last-flight')}</th>
                            <th>{t('actions')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {pilots.length === 0 ? (
                            <tr className="background-change">
                                <td colSpan="10">{t('no-pilots-found')}</td>
                            </tr>
                        ) : (
                            pilots.map((pilot) => (
                                <tr key={pilot.id} className="background-change">
                                    <td>{pilot.id}</td>
                                    <td>{pilot.firstName}</td>
                                    <td>{pilot.lastName}</td>
                                    <td>{pilot.email}</td>
                                    <td>{pilot.callsign || 'N/A'}</td>
                                    <td>{pilot.ivaoId || 'N/A'}</td>
                                    <td>{pilot.vatsimId || 'N/A'}</td>
                                    <td>
                                        <span className={pilot.onVacation ? 'badge warning' : 'badge success'}>
                                            {pilot.onVacation ? 'Vacation' : 'Active'}
                                        </span>
                                    </td>
                                    <td>{formatLastFlight(pilot.flights)}</td>
                                    <td>
                                        <button
                                            className="btn primary"
                                            onClick={() => handleEdit(pilot)}
                                            title={t('edit-pilot')}
                                            style={{ marginRight: '5px' }}
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button
                                            className={pilot.onVacation ? "btn success" : "btn warning"}
                                            onClick={() => toggleVacation(pilot.id, pilot.onVacation)}
                                            title={pilot.onVacation ? t('return-from-vacation') : t('set-to-vacation')}
                                            style={{ marginRight: '5px' }}
                                        >
                                            <FontAwesomeIcon icon={pilot.onVacation ? faBriefcase : faUmbrellaBeach} />
                                        </button>
                                        <button
                                            className="btn danger"
                                            onClick={() => handleDelete(pilot.id)}
                                            title={t('delete-pilot')}
                                        >
                                            <FontAwesomeIcon icon={faTrashCan} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            </Role>

            {/* Edit Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={handleCancelEdit}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{t('edit-pilot-data')}</h3>
                            <button className="modal-close" onClick={handleCancelEdit}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('first-name')}</label>
                                    <input
                                        type="text"
                                        value={editForm.firstName}
                                        onChange={(e) => handleEditChange('firstName', e.target.value)}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('last-name')}</label>
                                    <input
                                        type="text"
                                        value={editForm.lastName}
                                        onChange={(e) => handleEditChange('lastName', e.target.value)}
                                        className="form-input"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{t('email')}</label>
                                <input
                                    type="email"
                                    value={editForm.email}
                                    onChange={(e) => handleEditChange('email', e.target.value)}
                                    className="form-input"
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('callsign')}</label>
                                {!editForm.callsign && loadingCallsigns ? (
                                    <div className="loading-callsigns">
                                        <FontAwesomeIcon icon={faSpinner} spin /> {t('loading-available-callsigns')}
                                    </div>
                                ) : !editForm.callsign && availableCallsigns.length > 0 ? (
                                    <Select
                                        options={availableCallsigns.map(cs => ({
                                            value: cs,
                                            label: `${globalVariables.OACI}${cs}`
                                        }))}
                                        value={editForm.callsign && editForm.callsign.length === 3 ? {
                                            value: editForm.callsign,
                                            label: `${globalVariables.OACI}${editForm.callsign}`
                                        } : null}
                                        onChange={(selectedOption) => {
                                            // Save the complete callsign with prefix (EXU001)
                                            handleEditChange('callsign', selectedOption ? `${globalVariables.OACI}${selectedOption.value}` : '');
                                        }}
                                        placeholder={t('select-available-callsign')}
                                        isClearable
                                        className="callsign-select"
                                        classNamePrefix="react-select"
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={editForm.callsign}
                                        onChange={(e) => handleEditChange('callsign', e.target.value)}
                                        className="form-input"
                                        placeholder={`${globalVariables.OACI}###`}
                                        readOnly={editForm.callsign !== ''}
                                    />
                                )}
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>IVAO ID</label>
                                    <input
                                        type="text"
                                        value={editForm.ivaoId}
                                        onChange={(e) => handleEditChange('ivaoId', e.target.value)}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>VATSIM ID</label>
                                    <input
                                        type="text"
                                        value={editForm.vatsimId}
                                        onChange={(e) => handleEditChange('vatsimId', e.target.value)}
                                        className="form-input"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button 
                                className="btn success"
                                onClick={() => handleSaveEdit(editingPilot)}
                            >
                                <FontAwesomeIcon icon={faSave} /> {t('save-changes')}
                            </button>
                            <button 
                                className="btn warning"
                                onClick={handleCancelEdit}
                            >
                                <FontAwesomeIcon icon={faTimes} /> {t('cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}